import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ProgressIndicatorProps {
  currentStep: number;
  totalSteps: number;
}

const steps = [
  { number: 1, name: 'Welcome', icon: '👋' },
  { number: 2, name: 'Create Site', icon: '🌐' },
  { number: 3, name: 'Languages', icon: '🗣️' },
  { number: 4, name: 'Integration', icon: '🔌' },
  { number: 5, name: 'Install', icon: '💻' },
  { number: 6, name: 'Complete', icon: '🎉' },
];

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  currentStep,
  totalSteps,
}) => {
  return (
    <div className="w-full">
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
          <span>Step {currentStep} of {totalSteps}</span>
          <span>{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-gradient-to-r from-primary to-primary/70 h-2 rounded-full transition-all duration-500"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Indicators */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center flex-1">
            {/* Step Circle */}
            <div className="flex flex-col items-center flex-shrink-0">
              <div
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all duration-300",
                  step.number < currentStep && "bg-green-500 text-white",
                  step.number === currentStep && "bg-primary text-primary-foreground ring-4 ring-primary/20",
                  step.number > currentStep && "bg-muted text-muted-foreground"
                )}
              >
                {step.number < currentStep ? (
                  <Check className="w-5 h-5" strokeWidth={3} />
                ) : (
                  step.icon
                )}
              </div>
              <div
                className={cn(
                  "mt-2 text-xs font-medium text-center",
                  step.number <= currentStep ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {step.name}
              </div>
            </div>

            {/* Connecting Line */}
            {index < steps.length - 1 && (
              <div className="flex-1 mx-2">
                <div
                  className={cn(
                    "h-1 rounded transition-all duration-300",
                    step.number < currentStep ? "bg-green-500" : "bg-muted"
                  )}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgressIndicator;
